import { Location } from './types';

/**
 * Calculate distance between two points using Haversine formula
 * @param lat1 Latitude of first point
 * @param lon1 Longitude of first point
 * @param lat2 Latitude of second point
 * @param lon2 Longitude of second point
 * @returns Distance in kilometers
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return Math.round(distance * 100) / 100; // Round to 2 decimal places
}

/**
 * Check if user is within specified distance from work location
 * @param currentLocation User's current location
 * @param workLocation User's work location
 * @param maxDistance Maximum allowed distance in kilometers
 * @returns Object with isWithinRange and distance
 */
export function isWithinWorkRange(
  currentLocation: Location,
  workLocation: Location,
  maxDistance: number = 1
): { isWithinRange: boolean; distance: number } {
  const distance = calculateDistance(
    currentLocation.latitude,
    currentLocation.longitude,
    workLocation.latitude,
    workLocation.longitude
  );
  
  return {
    isWithinRange: distance <= maxDistance,
    distance
  };
}

/**
 * Check if user is within range of ANY company work location
 * @param currentLocation User's current location
 * @param workLocations Array of all company work locations
 * @param maxDistance Maximum allowed distance in kilometers
 * @returns Object with isWithinRange, nearestLocation, and distance to nearest location
 */
export function isWithinAnyWorkRange(
  currentLocation: Location,
  workLocations: Array<{ id: string; name: string; address: string; latitude: number; longitude: number }>,
  maxDistance: number = 1
): { 
  isWithinRange: boolean; 
  nearestLocation: { id: string; name: string; address: string; latitude: number; longitude: number } | null;
  distance: number;
} {
  if (!workLocations || workLocations.length === 0) {
    return {
      isWithinRange: false,
      nearestLocation: null,
      distance: Infinity
    };
  }

  let nearestLocation = null;
  let shortestDistance = Infinity;

  // Check distance to each work location
  for (const location of workLocations) {
    // Skip invalid locations
    if (typeof location.latitude !== 'number' || typeof location.longitude !== 'number' ||
        isNaN(location.latitude) || isNaN(location.longitude)) {
      continue;
    }

    const distance = calculateDistance(
      currentLocation.latitude,
      currentLocation.longitude,
      location.latitude,
      location.longitude
    );

    if (distance < shortestDistance) {
      shortestDistance = distance;
      nearestLocation = location;
    }
  }

  return {
    isWithinRange: shortestDistance <= maxDistance,
    nearestLocation,
    distance: shortestDistance
  };
}

/**
 * Get current location using browser's Geolocation API
 * @returns Promise with current location
 */
export function getCurrentLocation(): Promise<Location> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        reject(new Error(`Geolocation error: ${error.message}`));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  });
}

/**
 * Format date to readable string
 * @param date Date to format
 * @returns Formatted date string
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

/**
 * Format distance for display
 * @param distance Distance in kilometers
 * @returns Formatted distance string
 */
export function formatDistance(distance: number): string {
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m`;
  }
  return `${distance}km`;
}

/**
 * Get address from coordinates using reverse geocoding
 * @param latitude Latitude coordinate
 * @param longitude Longitude coordinate
 * @returns Promise with address string
 */
export async function getAddressFromCoordinates(
  latitude: number,
  longitude: number
): Promise<string> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
      {
        headers: {
          'Accept-Language': 'en-US,en;q=0.9',
          'User-Agent': 'MeconPersonel/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch address');
    }

    const data = await response.json();
    
    if (data.display_name) {
      // Extract the most relevant parts of the address
      const addressParts = data.display_name.split(', ');
      // Return first 3 parts for a cleaner address
      return addressParts.slice(0, 3).join(', ');
    }
    
    return 'Address not available';
  } catch (error) {
    console.error('Error getting address:', error);
    return 'Address not available';
  }
}

/**
 * Get current location with address using browser's Geolocation API
 * @returns Promise with current location including address
 */
export async function getCurrentLocationWithAddress(): Promise<Location> {
  const location = await getCurrentLocation();
  const address = await getAddressFromCoordinates(location.latitude, location.longitude);
  
  return {
    ...location,
    address
  };
}

/**
 * Get current date in Turkey timezone
 * @returns Date object in Turkey timezone
 */
export function getTurkeyDate(): Date {
  // For users already in Turkey, this will be their local time
  return new Date();
}

/**
 * Check if a date is today in Turkey timezone
 * @param date Date to check
 * @returns boolean indicating if the date is today
 */
export function isTodayInTurkey(date: Date): boolean {
  // Ensure we have a proper Date object
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const today = new Date();
  const checkDate = new Date(dateObj);
  
  return today.getFullYear() === checkDate.getFullYear() &&
         today.getMonth() === checkDate.getMonth() &&
         today.getDate() === checkDate.getDate();
}

/**
 * Format date in Turkey timezone
 * @param date Date to format
 * @returns Formatted date string in Turkey timezone
 */
export function formatTurkeyDate(date: Date): string {
  // Ensure we have a proper Date object
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // For users in Turkey, use their local timezone
  return new Intl.DateTimeFormat('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(dateObj);
}

/**
 * Get coordinates from address using geocoding
 * @param address Address to geocode
 * @returns Promise with coordinates
 */
export async function getCoordinatesFromAddress(address: string): Promise<{ latitude: number; longitude: number }> {
  try {
    console.log('Fetching coordinates for address:', address);
    
    // Clean up the address for better results with Turkish addresses
    let cleanAddress = address
      .replace(/Cd\./g, 'Caddesi')
      .replace(/Sok\./g, 'Sokak')
      .replace(/Mah\./g, 'Mahallesi')
      .replace(/No\./g, 'No')
      .trim();
    
    // Add Turkey if not present
    if (!cleanAddress.toLowerCase().includes('türkiye') && !cleanAddress.toLowerCase().includes('turkey')) {
      cleanAddress += ', Turkey';
    }
    
    console.log('Cleaned address:', cleanAddress);
    
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cleanAddress)}&limit=1&countrycodes=tr`,
      {
        headers: {
          'Accept-Language': 'tr,en-US,en;q=0.9',
          'User-Agent': 'MeconPersonel/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Nominatim response:', data);
    
    if (data && data.length > 0) {
      const lat = parseFloat(data[0].lat);
      const lon = parseFloat(data[0].lon);
      
      if (!isNaN(lat) && !isNaN(lon)) {
        console.log('Found coordinates:', { latitude: lat, longitude: lon });
        return {
          latitude: lat,
          longitude: lon
        };
      }
    }
    
    throw new Error(`No coordinates found for address: ${address}`);
  } catch (error) {
    console.error('Error getting coordinates:', error);
    // Provide a more descriptive error message
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    throw new Error(`Failed to get coordinates: ${errorMessage}`);
  }
}

// Utility function to get user's IP address
export const getUserIP = async (): Promise<string> => {
  try {
    // Use a public IP service
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP address:', error);
    throw new Error('Unable to determine IP address');
  }
}; 